import { useMemo, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import some from "lodash-es/some"
import { type Location, type PathMatch, matchPath, useLocation } from "react-router-dom"
import { useUpdateEffect } from "react-use"

import { ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import { ERoute } from "@/3514/constants"
import { type TUserState, useIsPostSessionModalEnabled, useUser } from "@/3514/hooks"
import {
    EPostSessionModalMode,
    type TPostSessionInitialDataQuery,
    type TPostSessionModalProps,
    usePostSessionInitialDataQuery
} from "@/3514/modals/post-session"
import { coachRatingQueryPrefetcher } from "@/3514/queries"
import { EModalId, type TModalDispatcher, useAnyModalVisible, useModalDispatcher } from "@/3514/store/modal"
import type { IUserParticipantModel } from "@/3514/types"
import { isSafariBrowser, isUserParticipant } from "@/3514/utils"
import { seconds } from "@/3514/utils/time.utils"
import { ParticipantUrls } from "@/constants"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

const PostSessionGuard: FC = (): ReactElement => {
    const qc: QueryClient = useQueryClient()

    const { user, isLoaded: isUserLoaded }: TUserState = useUser()

    const { pathname }: Location = useLocation()

    const isParticipant: boolean = isUserParticipant(user)

    const isProhibitedPathToShowModal: boolean = useMemo(
        (): boolean =>
            isParticipant
                ? some(
                      [
                          ParticipantUrls.OFFBOARDING,
                          ParticipantUrls.END_OF_JOURNEY_PAGE,
                          ParticipantUrls.WAITING_LIST,
                          ParticipantUrls.SURVEY_BY_CODE,
                          ERoute.Login,
                          ERoute.ResetPassword,
                          ERoute.Register,
                          ERoute.Participant_OnboardingProfile,
                          ERoute.Participant_OnboardingVideo,
                          ERoute.Participant_OnboardingModules_Legacy,
                          ERoute.Participant_OnboardingDiagnostic,
                          ERoute.Participant_OnboardingDiagnosticResults_Legacy,
                          ERoute.Participant_OnboardingReflection,
                          ERoute.Participant_OnboardingHolds,
                          ERoute.Participant_OnboardingCoach,
                          ERoute.Participant_OnboardingRedirect,
                          "privacy"
                      ],
                      (url: string): PathMatch<string> => matchPath(url, pathname)
                  )
                : true,
        [pathname, isParticipant]
    )

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const query: TPostSessionInitialDataQuery = usePostSessionInitialDataQuery({
        enabled: Boolean(
            isUserLoaded &&
                isParticipant &&
                (user as IUserParticipantModel).isOnboardingFinished &&
                !isProhibitedPathToShowModal
        )
    })

    const isAnyModalVisible: boolean = useAnyModalVisible()

    const isPostSessionModalEnabled: boolean = useIsPostSessionModalEnabled()

    const seenCoachingSessions: RefObject<number[]> = useRef<number[]>([])

    const shouldTrigger: boolean =
        isParticipant &&
        isPostSessionModalEnabled &&
        !isAnyModalVisible &&
        !query.isError &&
        !isEmpty(query.data) &&
        !isProhibitedPathToShowModal &&
        !includes<number>(seenCoachingSessions.current, query.data?.sessionId)

    useUpdateEffect((): TEmptyCallback => {
        if (!shouldTrigger) return emptyCallback

        const handler: TEmptyCallback = (): void => {
            void coachRatingQueryPrefetcher({ qc }, { participantId: user.id })

            seenCoachingSessions.current.push(query.data?.sessionId)

            modalDispatcher.showModal<TPostSessionModalProps>(EModalId.PostSession, {
                user: user as IUserParticipantModel,
                sessionId: query.data?.sessionId,
                moduleId: query.data?.module?.id,
                moduleTitle: query.data?.module?.title,
                sessionsQueryKey: ECoachingSessionsIndividualQueryKey({ participantId: user.id }),
                mode: EPostSessionModalMode.PastReflection
            })
        }

        // Resolved here rather than at module scope. Reading it from the "@/3514/utils" barrel while
        // that barrel is still initialising gives undefined
        // (`TypeError: (0, utils_1.isSafariBrowser) is not a function`), and the value is only ever
        // needed inside this effect anyway - so there was nothing to gain from hoisting it.
        const isSafari: boolean = isSafariBrowser()

        const timeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
            if (isSafari) handler()
        }, seconds(15))

        if (!isSafari) requestIdleCallback(handler, { timeout: seconds(15) })

        return isSafari ? emptyCallback : (): void => void clearTimeout(timeout)
    }, [shouldTrigger, user, query.data, query.dataUpdatedAt])

    return null
}

PostSessionGuard.displayName = "PostSessionGuard"

export { PostSessionGuard }
